import type {
  Block, Cell, ChipCell, Column, KvBlock, KvItem, NoteBlock, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinor, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Storefront — the shelf, the margin report and the owed-order queue
 * (go-wallet#778, #824; this console #136).
 *
 *   sf-catalog  GET /v1/admin/storefront/items
 *   sf-margin   GET /v1/admin/storefront/margin
 *   sf-failed   GET /v1/admin/storefront/orders
 *               GET /v1/admin/storefront/orders/{orderRef}   (row opener)
 *
 * # `GET /storefront/orders` is not an order list, and that decides two screens
 *
 * `StorefrontQueueReader.Owed` binds `state = $1` from `queueState()`, which
 * walks `storefront.States()` and returns the SINGLE state for which `Owed()`
 * is true — `paid`. No parameter widens it. The `state` query parameter exists
 * only so a value can be REFUSED: `state=failed` is a 400 carrying
 * `ErrStorefrontFailedWithDebit`, because `transitions[paid]` is
 * `{delivered, refunded}` and an order that has been charged cannot be
 * abandoned. Every other non-owed state is refused by the same error.
 *
 * So this loader sends NO `state`, and `SENDS_NO_STATE_FILTER` is asserted by a
 * test — the failure it prevents is a deterministic 400 on every page load.
 *
 * The consequence is bigger than a parameter. `sf-failed`'s defining rule is
 * the design brief's §6.10 alarm — "no state may exist where money was debited
 * and there is neither a voucher nor a refund" — and that IS the query's
 * selection criterion, exactly. `sf-orders` asks for the lifecycle ACROSS
 * `created → paid → delivered / failed → refunded`, and no read in this build
 * returns a delivered, refunded or created order in a list. Wiring it here
 * would draw a table headed "Orders", footed with a count, whose every row is
 * `paid` by construction — the section's order book misrepresented as complete
 * because it was the nearest available read. `sf-orders` is therefore absent
 * from `SCREEN_LOADERS` and stays not-built.
 *
 * # What the design draws that these reads do not send — DROPPED
 *
 * The rule is `./aml.ts`'s: a column the endpoint cannot answer for is dropped,
 * never zero-filled and never invented, and every drop is named in the screen's
 * own provenance so the badge says `Partial` and the operator is told which
 * parts of the designed screen this read cannot answer for. Eleven columns go.
 *
 *   - `sf-catalog` loses Countries and VAT treatment — neither is a property of
 *     a shelf item. Whether a country may buy is jurisdiction gating (`cf-juris`)
 *     and the tax treatment is a rate row for the BUYER's country resolved at
 *     order time (`sf-vat`). It also loses Supplier cost and Margin, which are
 *     Finance figures behind `reports.financial.read` while this route answers
 *     to `storefront.order.read`. Serving them here would route around a
 *     permission rather than add a column.
 *   - `sf-margin` loses its gross-margin TOTAL. No total is served, and the
 *     absence is a decision: a sum over these margins is a sum of one-unit
 *     differences between two price lists, weighted by nothing anybody sold,
 *     and beside real money it reads as money that moved.
 *   - `sf-failed` loses Debit posting, Voucher, Refund and Supplier error. The
 *     first is refused by the endpoint by name — a standalone card purchase
 *     touches no ledger, wallet or account and produces no posting, and one
 *     here would be an operator issuing e-money against a card payment. Voucher
 *     and Refund have no field AND are constant by construction: every row in
 *     this queue is un-vouchered and un-refunded, so a column of `none` is not
 *     information. The supplier error lives on the order's history, which is
 *     why the history is a row opener rather than a column.
 *
 * # `sf-margin` is the law-12 trap, and the corpus draws it wrong
 *
 * The corpus block is `src: 'analytics'` with a `fresh:` stamp and the note
 * "ties to supplier_costs in the ledger". All three are wrong. The read is
 * Postgres — `giftcard_suppliers`, `giftcard_supply_offers`,
 * `giftcard_supply_refreshes` — so the block is `src: 'ledger'`; there is no
 * ClickHouse mirror behind any figure here. And `supplier_costs` is a ledger
 * account class with no writer: this read touches no journal row and cannot be
 * tied to one.
 *
 * The freshness that DOES exist is a different thing and is per supplier — how
 * long before `as_of` we last managed to ASK a supplier for its prices.
 * `catalogue_age_known: false` renders as "nobody has ever asked", never as a
 * zero age, because a supplier unreachable for a week looks exactly like a
 * supplier whose prices have not moved.
 *
 * In this build an EMPTY margin screen is the expected state: no vendor is
 * contracted and no supplier adapter is configured, so every refresh attempt
 * fails and no offer has ever been recorded. The empty text says "we have never
 * been able to ask", never "our margin is zero".
 *
 * # Read-only, and there is nothing to gate
 *
 * All four routes are reads and every handler says so. `MarkDelivered`,
 * `MarkFailed` and `MarkRefunded` exist with no non-test caller — they wait on
 * the gift-card supply seam (go-wallet#78). Nothing here resolves, retries,
 * refunds, publishes, re-prices or re-sources, so there is no control for law
 * 18 to show disabled: `sf-margin` is refused at the SCREEN by
 * `reports.financial.read`, which is the denied state rather than a hidden
 * button.
 */

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

export const STOREFRONT_ITEMS_PATH = '/storefront/items'
export const STOREFRONT_MARGIN_PATH = '/storefront/margin'
export const STOREFRONT_ORDERS_PATH = '/storefront/orders'

export function storefrontOrderPath(orderRef: string): string {
  return `${STOREFRONT_ORDERS_PATH}/${encodeURIComponent(orderRef)}`
}

const CATALOG_SOURCE = 'postgres · storefront_items (GET /v1/admin/storefront/items)'
const MARGIN_SOURCE = 'postgres · giftcard_supply_offers (GET /v1/admin/storefront/margin)'
const QUEUE_SOURCE = 'postgres · storefront_orders (GET /v1/admin/storefront/orders)'

/** Our own shelf and our own order records. */
const OPERATIONAL = 'operational'
/**
 * The buy price is the SUPPLIER's word, not ours.
 *
 * Worth saying in the badge rather than folding into `operational`: a face
 * value is a price we set and a buy price is a price somebody quoted us, and
 * the margin between them is only as good as the second. A supplier whose
 * catalogue we have not been able to read for a month is answerable for a
 * figure this screen still draws.
 */
const SUPPLY_AUTHORITY =
  'operational — face values ours, buy prices as the supplier stated them'
/** The PSP's reference is evidence money moved, never a claim that we moved it. */
const QUEUE_AUTHORITY = "operational — payment references are the payer's own, opaque to us"

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

/**
 * `Array.isArray` narrows to `any[]`, which spreads `any` through everything it
 * touches. Landing back on `readonly unknown[]` keeps every element a thing the
 * parse has to prove something about.
 */
function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * `word in TABLE` walks the prototype chain, so `constructor`, `toString` and
 * `__proto__` all answer true against any object literal — and the lookup that
 * follows returns a FUNCTION rather than a tone. An unrecognised value would
 * come back "recognised", chip itself with `Object.prototype.toString`, record
 * no gap, and leave the screen badging itself live over a word it does not know.
 */
function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

/**
 * An integer we can state exactly, or the reason we cannot.
 *
 * Three states, not two: absent and unusable are different facts about
 * different bugs. An id past `Number.MAX_SAFE_INTEGER` has already lost digits
 * in `JSON.parse` — printing it shows an identifier wrong in its last places
 * and entirely plausible, which is how an operator opens the wrong order.
 */
type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

function readInt(o: Record<string, unknown>, key: string): IntRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

/**
 * A duration in seconds. A negative one is unreadable rather than clamped.
 *
 * Clamping is the tempting move and it is a fabricated zero: a negative age
 * means the clocks disagree, and "0s" tells the operator the money arrived this
 * second. Two different incidents, one of them invisible.
 */
function readDuration(o: Record<string, unknown>, key: string): IntRead {
  const read = readInt(o, key)
  return read.kind === 'exact' && read.value < 0 ? { kind: 'unreadable' } : read
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * An RFC-3339 instant, in the format the design draws timestamps in.
 *
 * The epoch guard is not paranoia. Several fields here are plain `time.Time`
 * with no `omitempty`, so a zero value reaches the wire as
 * `0001-01-01T00:00:00Z` and parses perfectly well into a date in the year 1 —
 * which renders as a timestamp rather than as an absence.
 */
function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/**
 * A span, from the server's own arithmetic. No ambient clock here.
 *
 * Truncated, never rounded up. An order four hours and one minute old shown as
 * "5h" overstates the alarm; one shown as "4h" when it is 4h 59m understates
 * it. Past two days the minutes are noise and the days are what gets escalated.
 */
function spanOf(seconds: number): string {
  const s = Math.trunc(seconds)
  if (s < 60) return `${String(s)}s`
  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${String(minutes)}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${String(hours)}h ${String(minutes % 60).padStart(2, '0')}m`
  return `${String(Math.floor(hours / 24))}d ${String(hours % 24)}h`
}

/**
 * An amount cell, or a stated absence. Never a bare number — law 8.
 *
 * Both halves must read. A minor value with no currency is the one figure on
 * these screens somebody might quote to a supplier or a buyer, and a currency
 * with no amount is not an amount. `exactMinor` reads both the quoted-decimal
 * form `minorUnits` marshals and the bare JSON integer the queue view emits,
 * and refuses anything past 2^53-1 — where `JSON.parse` has already dropped
 * digits and no check downstream can recover them.
 */
function amountCell(raw: unknown, currency: unknown): Cell {
  const minor = exactMinor(raw)
  if (minor === null) return '— not a figure this console can state'
  if (!isCurrencyCode(currency)) return '— no currency on this amount'
  return [minor, currency.trim().toUpperCase()]
}

/** The `{amount_minor, currency}` pair the catalogue and margin reads use. */
function moneyCell(o: Record<string, unknown>, key: string): Cell {
  const m = asRecord(o[key])
  if (m === null) return '— not stated'
  return amountCell(m['amount_minor'], m['currency'])
}

/** Whether a `moneyView` read cleanly, for gap accounting. */
function moneyReadable(o: Record<string, unknown>, key: string): boolean {
  const m = asRecord(o[key])
  if (m === null) return false
  return exactMinor(m['amount_minor']) !== null && isCurrencyCode(m['currency'])
}

/** Groups repeated gaps so the provenance chip stays readable at 200 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

/**
 * A boolean as a chip, with the two labels named by the caller.
 *
 * A boolean that did not arrive is `unstated` with an attention tone, never
 * `false`. "Not available" and "we could not read whether it is available" are
 * different sentences on a shelf an operator is deciding whether to trust.
 */
function boolChip(
  value: boolean | null, whenTrue: string, whenFalse: string, trueTone: Tone, falseTone: Tone,
): ChipCell {
  if (value === null) return ['unstated', 'a']
  return value ? [whenTrue, trueTone] : [whenFalse, falseTone]
}

// ---------------------------------------------------------------------------
// The read, resolved into an answer either way
// ---------------------------------------------------------------------------

export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      readonly decisionRef: string | null
      readonly refused: boolean
    }

/**
 * An `AbortError` is rethrown: a navigation away is a cancellation, not a
 * degraded screen, and swallowing it would render "this could not be read" over
 * a screen the operator has already left.
 */
async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return { ok: false, reason: `${subject} could not be reached.`, decisionRef: null, refused: false }
  }
}

/**
 * How a refusal is quoted, and what is said when it cannot be.
 *
 * Law 13 — every refusal surfaces a `decision_ref`. One that arrives without
 * one leaves an operator with nothing to put in an escalation, and the console
 * says exactly that rather than inventing a reference or staying quiet.
 *
 * The `decision_ref` in the `ref` line is also what keeps this note off
 * `withoutRoadmapNotes`' essay filter, which is deliberate: a refusal is the
 * one long note an operator does have to read.
 */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

/** The screen when nothing was read at all. One note, and no table under it. */
function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  const note: NoteBlock = {
    t: 'note', tone: 'r', kicker, title,
    body: `${failed.reason} ${body}`,
    ref: refLine(failed, 'Nothing on this screen was read.'),
  }
  return {
    blocks: [note],
    // `unread`, not `partial`: nothing here is real, and "partial" claims some
    // of it is. There is deliberately no `readAt` — stamping the attempt would
    // date figures that do not exist.
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

// ===========================================================================
// sf-catalog — the shelf
// ===========================================================================

/**
 * The brand ladder, one row per (brand, currency).
 *
 * Currency is a GROUP KEY and is never folded — law 8, and the backend refuses
 * the fold for the same reason. Two items under one brand may be priced in
 * different currencies, and a merged ladder reading "25 / 25 / 50" over two of
 * them is a currency-less comparison that looks like one price list and is
 * several.
 *
 * The corpus's Countries, VAT treatment, Supplier cost and Margin columns are
 * gone; `Sourced` replaces them and is the column an operator acts on.
 */
const BRAND_COLS: readonly Column[] = [
  ['Brand', 'x2', 'minmax(170px,1.2fr)'],
  ['Currency', 'm', '96px'],
  ['Denominations', 'x', 'minmax(200px,1.4fr)'],
  ['Items', 'n', '86px'],
  ['Available', 'n', '106px'],
  ['Sourced', 'n', '100px'],
]

interface BrandRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
}

function readBrandGroup(raw: unknown): BrandRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const brand = readText(o, 'brand')
  if (brand === null) gaps.push('a brand name')

  // No currency means no ladder this console will draw. A denomination list is
  // a list of amounts, and amounts without their unit are the exact rendering
  // law 8 refuses.
  const currency = readText(o, 'currency')
  const usable = currency !== null && isCurrencyCode(currency)
  if (!usable) gaps.push('a currency for a brand group, so its ladder is withheld')

  const ladderRaw = asArray(o['face_values_minor'])
  let ladder = '— not stated'
  if (ladderRaw === null) {
    gaps.push('a denomination ladder')
  } else if (usable) {
    const parts: string[] = []
    let lost = 0
    for (const v of ladderRaw) {
      const minor = exactMinor(v)
      if (minor === null) lost += 1
      else parts.push(String(minor))
    }
    if (lost > 0) gaps.push('a denomination this console could not state')
    // Minor units with the code beside them, never a major-unit conversion
    // here: the scale belongs to `src/lib/money` and a second implementation of
    // it in a label is where the third decimal place goes missing.
    ladder = parts.length === 0
      ? '— none stated'
      : `${parts.join(' / ')} minor · ${currency.toUpperCase()}`
  }

  const items = readInt(o, 'items')
  if (items.kind !== 'exact') gaps.push('an item count for a brand')
  const available = readInt(o, 'available')
  if (available.kind !== 'exact') gaps.push('an available count for a brand')
  const sourced = readInt(o, 'sourced')
  if (sourced.kind !== 'exact') gaps.push('a sourced count for a brand')

  return {
    cells: [
      brand ?? '— unnamed',
      usable ? currency.toUpperCase() : '— none',
      ladder,
      items.kind === 'exact' ? String(items.value) : '—',
      available.kind === 'exact' ? String(available.value) : '—',
      sourced.kind === 'exact' ? String(sourced.value) : '—',
    ],
    gaps,
  }
}

const ITEM_COLS: readonly Column[] = [
  ['Item', 'm', 'minmax(170px,1.2fr)'],
  ['Brand', 'x', '140px'],
  ['Version', 'n', '86px'],
  ['Face value', 'a', '150px'],
  ['On sale', 'c', '116px'],
  ['Price in force', 'c', '134px'],
  ['Effective from', 'm', '128px'],
  ['Supply', 'c', 'minmax(190px,1.3fr)'],
  ['Published by', 'x', '140px'],
]

interface ItemRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  /** A live link that pins a superseded version: the shelf has no supply. */
  readonly misSourced: boolean
}

/**
 * The supply cell — three distinct answers, and none of them is a zero.
 *
 * An item with no `sourcing` block is one NOBODY HAS ARRANGED SUPPLY FOR. A
 * zeroed cost or an empty supplier would claim somebody would fulfil it for
 * nothing, which beside a margin column reads as 100% margin on a card that
 * cannot be bought.
 *
 * `sources_this_version: false` is the finding. One live link governs a product
 * across all its versions while its foreign key pins ONE of them, so re-pricing
 * an item leaves the link aimed at the withdrawn price: the item on sale has no
 * source and the row still names a supplier.
 */
function supplyCell(o: Record<string, unknown>, gaps: string[]): { cell: ChipCell; misSourced: boolean } {
  const s = asRecord(o['sourcing'])
  if (s === null) return { cell: ['no supply arranged', 'a'], misSourced: false }

  const key = readText(s, 'supplier_key')
  if (key === null) gaps.push('a supplier key on a supply link')
  const label = key ?? 'an unnamed supplier'

  const disabled = readBool(s, 'supplier_disabled')
  if (disabled === null) gaps.push('whether a linked supplier is still live')

  const sourcesThis = readBool(s, 'sources_this_version')
  if (sourcesThis === null) gaps.push('whether a supply link covers the version on sale')

  // Order matters: a link aimed at a superseded version is the more urgent
  // finding, because the row otherwise looks perfectly sourced.
  if (sourcesThis === false) {
    return { cell: [`${label} · pinned to an older version`, 'r'], misSourced: true }
  }
  if (disabled === true) return { cell: [`${label} · supplier withdrawn`, 'r'], misSourced: false }
  if (sourcesThis === null || disabled === null) return { cell: [`${label} · partly read`, 'a'], misSourced: false }
  return { cell: [label, 'g'], misSourced: false }
}

function readCatalogItem(raw: unknown): ItemRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const key = readText(o, 'item_key')
  if (key === null) gaps.push('an item key')
  const brand = readText(o, 'brand')
  if (brand === null) gaps.push('a brand')
  const display = readText(o, 'display_name')

  const version = readInt(o, 'version')
  if (version.kind !== 'exact') gaps.push('an item version')

  if (!moneyReadable(o, 'face_value')) gaps.push('a face value')

  // `available` and `in_force` are SEPARATE facts and neither implies the
  // other. An item can be in the live interval and switched off — "out of stock
  // this week" is reversible without minting a version, "this price is history"
  // is not — and folding them loses the distinction the schema keeps.
  const available = readBool(o, 'available')
  if (available === null) gaps.push('whether an item is on sale')
  const inForce = readBool(o, 'in_force')
  if (inForce === null) gaps.push('whether an item version is the one in force')

  const from = readInstant(o, 'effective_from')
  if (from.kind !== 'at') gaps.push('an effective-from date')

  const publishedBy = readText(o, 'published_by')
  if (publishedBy === null) gaps.push('who published an item version')

  const supply = supplyCell(o, gaps)

  return {
    cells: [
      display === null ? (key ?? '— unnamed') : `${key ?? '—'} · ${display}`,
      brand ?? '—',
      version.kind === 'exact' ? String(version.value) : '—',
      moneyCell(o, 'face_value'),
      boolChip(available, 'on sale', 'switched off', 'g', 'a'),
      boolChip(inForce, 'in force', 'superseded', 'g', 'n'),
      from.kind === 'at' ? from.label : '—',
      supply.cell,
      publishedBy ?? '—',
    ],
    gaps,
    misSourced: supply.misSourced,
  }
}

/**
 * The columns of the designed screen this read cannot answer for.
 *
 * Named in operator language rather than in the payload's, because this list is
 * read on screen by somebody deciding whether they can act on what is in front
 * of them.
 */
const CATALOG_MISSING: readonly string[] = [
  'the countries an item may be sold in — not a property of the shelf; whether a country may buy at all is jurisdiction gating',
  'VAT treatment — a rate row for the buyer’s country resolved at order time, on VAT/OSS rates',
  'supplier cost and margin — Finance figures on Supplier costs & margin, behind a permission this read does not answer to',
]

export function composeStorefrontCatalog(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Brand catalog', 'The shelf could not be read',
      'Nothing below is known — that is different from nothing being published, and the screen shows none rather than guessing at any.',
      read, CATALOG_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const items = o === null ? null : asArray(o['items'])
  const brands = o === null ? null : asArray(o['brands'])
  if (o === null || items === null || brands === null) {
    return unread(
      'Brand catalog', 'The shelf could not be read',
      'The catalogue returned a shape this console does not know.',
      { ok: false, reason: 'The catalogue returned a shape this console does not know.', decisionRef: null, refused: false },
      CATALOG_SOURCE,
    )
  }

  const gaps: string[] = []
  let unreadableBrands = 0
  const brandRows: (readonly Cell[])[] = []
  for (const raw of brands) {
    const row = readBrandGroup(raw)
    if (row === null) unreadableBrands += 1
    else { brandRows.push(row.cells); gaps.push(...row.gaps) }
  }

  let unreadableItems = 0
  let misSourced = 0
  const itemRows: (readonly Cell[])[] = []
  for (const raw of items) {
    const row = readCatalogItem(raw)
    if (row === null) unreadableItems += 1
    else {
      itemRows.push(row.cells)
      gaps.push(...row.gaps)
      if (row.misSourced) misSourced += 1
    }
  }

  const asOf = readInstant(o, 'as_of')
  if (asOf.kind !== 'at') gaps.push('the instant “in force” was computed against')

  const truncated = readBool(o, 'brands_truncated') === true
  const hasMore = readBool(o, 'has_more') === true

  // An empty catalogue means NOTHING HAS BEEN PUBLISHED — the table ships empty
  // by design and only an operator's publish writes it. The corpus's empty text
  // says "supplier availability drives this list", which is the opposite claim
  // and the one that sends somebody to chase a supplier.
  const brandFooter = brandRows.length === 0
    ? (unreadableBrands === 0
        ? 'Nothing has been published to the shelf. The catalogue ships empty and only a publish writes it — this is not a supplier being out of stock.'
        : `No brand group could be read: ${String(unreadableBrands)} came back in a shape this console does not know.`)
    : `${String(brandRows.length)} brand ${brandRows.length === 1 ? 'group' : 'groups'} · one row per brand and currency${truncated ? ' · more groups exist than the backend summarised' : ''}`

  const itemFooter = itemRows.length === 0
    ? (unreadableItems === 0
        ? 'No catalogue item on this page. Nothing has been published, which is not the same as nothing being in stock.'
        : `No item could be read: ${String(unreadableItems)} came back in a shape this console does not know.`)
    : `${String(itemRows.length)} ${itemRows.length === 1 ? 'item' : 'items'} shown${hasMore ? ' · the backend caps the page, so this is not the whole catalogue' : ''}`

  const blocks: Block[] = [
    {
      t: 'table', title: 'Brands on the shelf', src: 'ledger', facets: false,
      cols: BRAND_COLS, rows: brandRows, footer: brandFooter,
    },
    {
      t: 'table', title: 'Catalogue items', src: 'ledger', facets: false,
      cols: ITEM_COLS, rows: itemRows, footer: itemFooter,
    },
  ]

  // The one finding on this screen an operator acts on today, and it is
  // invisible without saying so: the row names a supplier and the item on sale
  // has no source. Short, because a notice nobody reads is worse than none.
  if (misSourced > 0) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Supply',
      title: `${String(misSourced)} item${misSourced === 1 ? '' : 's'} on sale with no live supply`,
      body: 'The supply link names a supplier but pins an older version of the item, so the price on sale cannot be fulfilled. Re-link, or take the item off sale.',
      ref: 'sources_this_version = false',
    })
  }

  const missing = [...CATALOG_MISSING, ...summarise(gaps)]
  if (unreadableBrands > 0) missing.push(`${String(unreadableBrands)} brand group(s) this console could not read`)
  if (unreadableItems > 0) missing.push(`${String(unreadableItems)} catalogue item(s) this console could not read`)

  return {
    blocks,
    // Permanently `partial`, and that is the honest reading: `live` claims
    // everything on the designed screen was read, and four of its columns
    // cannot be.
    provenance: {
      kind: 'partial', source: CATALOG_SOURCE, authority: OPERATIONAL,
      readAt: asOf.kind === 'at'
        ? `${asOf.label} (the backend’s own as_of)`
        : `${readAtStamp(at)} (console receipt — the response did not state its as_of)`,
      missing,
    },
    title: 'Brand catalog',
    subject: `storefront_items · ${String(itemRows.length)} ${itemRows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ===========================================================================
// sf-margin — what a supplier said it would charge us
// ===========================================================================

const SUPPLIER_COLS: readonly Column[] = [
  ['Supplier', 'm', '150px'],
  ['Legal name', 'x', 'minmax(180px,1.3fr)'],
  ['Arrangement', 'c', '134px'],
  ['Catalogue last confirmed', 's', 'minmax(200px,1.3fr)'],
  ['Offers here', 'n', '110px'],
]

interface SupplierRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
}

/**
 * When we last managed to ASK this supplier for its prices.
 *
 * `catalogue_age_known: false` is "nobody has ever asked", and it renders as
 * that sentence rather than as a zero age or a blank. The distinction is the
 * whole point of the refresh table: a supplier unreachable for a week looks
 * exactly like a supplier whose prices have not moved.
 *
 * The attempt reported is the newest ATTEMPT, not the newest success — a
 * supplier whose reads have started failing has a stale catalogue AND a
 * problem, and reporting the last success would hide the second. So a failed
 * newest attempt is drawn red with its failure code, over an age that is still
 * how long ago we last got anything at all.
 */
function freshnessCell(o: Record<string, unknown>, gaps: string[]): Cell {
  const known = readBool(o, 'catalogue_age_known')
  if (known === null) {
    gaps.push('whether a supplier’s catalogue age is known')
    return ['— unstated']
  }
  if (!known) return ['never confirmed', 'no refresh has ever finished for this supplier']

  const r = asRecord(o['last_refresh'])
  if (r === null) {
    // The backend states these two travel together. One without the other is a
    // shape this console will not guess at, and it is a gap rather than a zero.
    gaps.push('a supplier’s last refresh, which was reported as known')
    return ['— unstated']
  }

  const age = readDuration(r, 'age_seconds')
  if (age.kind !== 'exact') gaps.push('how long ago a supplier’s catalogue was confirmed')
  const ok = readBool(r, 'ok')
  const outcome = readText(r, 'outcome')
  const failure = readText(r, 'failure_code')

  const label = age.kind === 'exact' ? `${spanOf(age.value)} ago` : '— age not stated'
  const sub = ok === false
    ? `last attempt failed${failure === null ? '' : ` · ${failure}`}`
    : ok === true
      ? `last attempt succeeded${outcome === null ? '' : ` · ${outcome}`}`
      : 'whether the last attempt succeeded was not stated'
  return [label, sub]
}

function readSupplier(raw: unknown): SupplierRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const key = readText(o, 'supplier_key')
  if (key === null) gaps.push('a supplier key')
  const name = readText(o, 'legal_name')
  if (name === null) gaps.push('a supplier legal name')

  // `live: false` is an arrangement we have stopped buying from. Its offers
  // stay listed — they are what every past margin is computed against — so
  // without this the prices read as supply we can still buy.
  const live = readBool(o, 'live')
  if (live === null) gaps.push('whether a supplier arrangement is still live')

  const offers = readInt(o, 'offer_count')
  if (offers.kind !== 'exact') gaps.push('a supplier’s offer count')

  return {
    cells: [
      key ?? '— unnamed',
      name ?? '—',
      boolChip(live, 'buying', 'stopped buying', 'g', 'a'),
      freshnessCell(o, gaps),
      // Zero is a real and common answer here — a registered arrangement whose
      // catalogue has never been read — and the supplier is listed carrying it
      // rather than omitted, so "we have a contract and no prices" is visible.
      offers.kind === 'exact' ? String(offers.value) : '—',
    ],
    gaps,
  }
}

/**
 * The per-group range, in basis points, and NO total.
 *
 * A sum over these margins is a sum of one-unit differences between two price
 * lists, weighted by nothing anybody sold; rendered beside real money it reads
 * as money that moved. The spread between the best and worst deal in a brand is
 * what the corpus's chart was actually reaching for, and basis points are
 * comparable across currencies where the amounts are not.
 */
const GROUP_COLS: readonly Column[] = [
  ['Supplier', 'm', '140px'],
  ['Brand', 'x2', 'minmax(150px,1.1fr)'],
  ['Currency', 'm', '96px'],
  ['Offers', 'n', '86px'],
  ['Listed', 'n', '86px'],
  ['In stock', 'n', '96px'],
  ['Margin range', 'x', 'minmax(170px,1.2fr)'],
]

const OFFER_COLS: readonly Column[] = [
  ['Supplier', 'm', '130px'],
  ['Product', 'm', 'minmax(160px,1.2fr)'],
  ['Version', 'n', '84px'],
  ['Face value', 'a', '146px'],
  ['Buy price', 'a', '146px'],
  ['Margin', 'a', '146px'],
  ['Margin bps', 'n', '110px'],
  ['Feed', 'c', '150px'],
]

interface MarginRead {
  readonly groupRows: readonly (readonly Cell[])[]
  readonly offerRows: readonly (readonly Cell[])[]
  readonly gaps: readonly string[]
  readonly unreadableGroups: number
  readonly unreadableOffers: number
  readonly lossMaking: number
}

/**
 * Basis points, as basis points.
 *
 * A POINTER on the wire, absent rather than zero when it cannot be computed:
 * zero basis points is a real margin — face equals buy — so a coalesced zero
 * would report the catalogue's worst deal as an arithmetic failure, or the
 * reverse. Absent is drawn as a stated absence and counted as a gap.
 */
function bpsCell(o: Record<string, unknown>, key: string, gaps: string[], gap: string): Cell {
  const read = readInt(o, key)
  if (read.kind === 'exact') return String(read.value)
  gaps.push(gap)
  return '— not computable'
}

function readOffer(
  raw: unknown, supplier: string, gaps: string[],
): { readonly cells: readonly Cell[]; readonly loss: boolean } | null {
  const o = asRecord(raw)
  if (o === null) return null

  const code = readText(o, 'supplier_product_code')
  if (code === null) gaps.push('a supplier product code')
  const name = readText(o, 'display_name')

  const version = readInt(o, 'version')
  if (version.kind !== 'exact') gaps.push('an offer version')

  if (!moneyReadable(o, 'face_value')) gaps.push('an offer face value')
  if (!moneyReadable(o, 'buy_price')) gaps.push('an offer buy price')
  if (!moneyReadable(o, 'margin')) gaps.push('an offer margin')

  // Negative where the supplier re-priced above the shelf face value, and drawn
  // as it computes: a loss-making offer is the only row on this screen anybody
  // has to act on today.
  const marginMoney = asRecord(o['margin'])
  const marginMinor = marginMoney === null ? null : exactMinor(marginMoney['amount_minor'])
  const loss = marginMinor !== null && marginMinor < 0

  // Two facts, deliberately not one: a product out of stock this week and one
  // the supplier has withdrawn from its feed call for opposite responses.
  const available = readBool(o, 'available')
  if (available === null) gaps.push('whether an offer is in stock')
  const listed = readBool(o, 'listed')
  if (listed === null) gaps.push('whether an offer is still in the supplier’s feed')
  const feed: ChipCell = listed === false
    ? ['withdrawn from the feed', 'r']
    : available === false
      ? ['listed · out of stock', 'a']
      : (listed === null || available === null)
        ? ['partly read', 'a']
        : ['listed · in stock', 'g']

  return {
    cells: [
      supplier,
      name === null ? (code ?? '— unnamed') : `${code ?? '—'} · ${name}`,
      version.kind === 'exact' ? String(version.value) : '—',
      moneyCell(o, 'face_value'),
      moneyCell(o, 'buy_price'),
      moneyCell(o, 'margin'),
      bpsCell(o, 'margin_bps', gaps, 'an offer margin in basis points'),
      feed,
    ],
    loss,
  }
}

function readGroups(raw: readonly unknown[]): MarginRead {
  const gaps: string[] = []
  const groupRows: (readonly Cell[])[] = []
  const offerRows: (readonly Cell[])[] = []
  let unreadableGroups = 0
  let unreadableOffers = 0
  let lossMaking = 0

  for (const entry of raw) {
    const g = asRecord(entry)
    if (g === null) { unreadableGroups += 1; continue }

    const supplier = readText(g, 'supplier_key')
    if (supplier === null) gaps.push('a supplier key on a margin group')
    const brand = readText(g, 'brand')
    if (brand === null) gaps.push('a brand on a margin group')

    // Currency is a GROUP KEY and never a summary of one. Two currencies under
    // one brand are two groups, and a merged "margin by brand" over them is a
    // subtraction of unlike numbers.
    const currency = readText(g, 'currency')
    if (currency === null || !isCurrencyCode(currency)) gaps.push('a currency on a margin group')

    const offers = readInt(g, 'offer_count')
    if (offers.kind !== 'exact') gaps.push('an offer count on a margin group')
    const listed = readInt(g, 'listed_count')
    if (listed.kind !== 'exact') gaps.push('a listed count on a margin group')
    const available = readInt(g, 'available_count')
    if (available.kind !== 'exact') gaps.push('an in-stock count on a margin group')

    // Pointers, so a group whose margins are all uncomputable says so rather
    // than reporting 0–0 bps, which is a real and very different answer.
    const low = readInt(g, 'margin_bps_low')
    const high = readInt(g, 'margin_bps_high')
    const range = low.kind === 'exact' && high.kind === 'exact'
      ? (low.value === high.value
          ? `${String(low.value)} bps`
          : `${String(low.value)} – ${String(high.value)} bps`)
      : '— no margin in this group could be computed'

    groupRows.push([
      supplier ?? '— unnamed',
      brand ?? '—',
      currency !== null && isCurrencyCode(currency) ? currency.toUpperCase() : '— none',
      offers.kind === 'exact' ? String(offers.value) : '—',
      listed.kind === 'exact' ? String(listed.value) : '—',
      available.kind === 'exact' ? String(available.value) : '—',
      range,
    ])

    const rawOffers = asArray(g['offers'])
    if (rawOffers === null) { gaps.push('the offers of a margin group'); continue }
    for (const o of rawOffers) {
      const offer = readOffer(o, supplier ?? '— unnamed', gaps)
      if (offer === null) unreadableOffers += 1
      else {
        offerRows.push(offer.cells)
        if (offer.loss) lossMaking += 1
      }
    }
  }

  return { groupRows, offerRows, gaps, unreadableGroups, unreadableOffers, lossMaking }
}

/**
 * What the designed screen asks for that this read does not serve.
 *
 * The first two are the law-12 correction and they are permanent: no order is
 * behind any figure here, and no total is served.
 */
const MARGIN_MISSING: readonly string[] = [
  'realised margin, and any total — no order is behind these figures; this read touches no ledger row and cannot be tied to one',
  'per-order margin and the gross storefront figure — the tables behind this read have no order path at all',
]

export function composeStorefrontMargin(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Supplier costs & margin', 'The supply catalogue could not be read',
      'No supplier, price or margin below is known — that is different from our margins being zero.',
      read, MARGIN_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const suppliersRaw = o === null ? null : asArray(o['suppliers'])
  const groupsRaw = o === null ? null : asArray(o['groups'])
  if (o === null || suppliersRaw === null || groupsRaw === null) {
    return unread(
      'Supplier costs & margin', 'The supply catalogue could not be read',
      'The margin read returned a shape this console does not know.',
      { ok: false, reason: 'The margin read returned a shape this console does not know.', decisionRef: null, refused: false },
      MARGIN_SOURCE,
    )
  }

  const gaps: string[] = []
  let unreadableSuppliers = 0
  const supplierRows: (readonly Cell[])[] = []
  for (const raw of suppliersRaw) {
    const row = readSupplier(raw)
    if (row === null) unreadableSuppliers += 1
    else { supplierRows.push(row.cells); gaps.push(...row.gaps) }
  }

  const margin = readGroups(groupsRaw)
  gaps.push(...margin.gaps)

  const asOf = readInstant(o, 'as_of')
  if (asOf.kind !== 'at') gaps.push('the instant the catalogue was resolved at')

  // The expected state of every deployment today: no vendor is contracted and
  // no supplier adapter is configured, so every refresh lands as `failed` and
  // no offer has ever been recorded. "We have never been able to ask" is a
  // completely different sentence from "our margin is zero", and only the first
  // is true.
  const supplierFooter = supplierRows.length === 0
    ? (unreadableSuppliers === 0
        ? 'No gift-card supplier is registered. No vendor is contracted and no supplier adapter is configured, so nothing has ever been asked for a price — this is not a margin of zero.'
        : `No supplier could be read: ${String(unreadableSuppliers)} came back in a shape this console does not know.`)
    : `${String(supplierRows.length)} ${supplierRows.length === 1 ? 'supplier' : 'suppliers'} · a supplier with no offers has a contract and no prices`

  const groupFooter = margin.groupRows.length === 0
    ? 'No supplier catalogue has ever been recorded, so there is no margin to range. This is “we have never been able to ask”, not “our margin is zero”.'
    : `${String(margin.groupRows.length)} ${margin.groupRows.length === 1 ? 'group' : 'groups'} · one row per supplier, brand and currency · range only, because a sum of unit margins is not money that moved`

  const offerFooter = margin.offerRows.length === 0
    ? 'No supply offer has ever been recorded.'
    : `${String(margin.offerRows.length)} ${margin.offerRows.length === 1 ? 'offer' : 'offers'} · margin is face value minus the supplier’s list price, per unit`

  const blocks: Block[] = [
    {
      // `ledger`, NOT `analytics`. The corpus draws this screen as an analytics
      // chart with a freshness stamp; the read is Postgres and there is no
      // ClickHouse mirror behind any figure on it — law 12 in the direction
      // that is easy to get wrong because the wrong answer looks more careful.
      t: 'table', title: 'Suppliers, and how fresh their prices are', src: 'ledger',
      facets: false, cols: SUPPLIER_COLS, rows: supplierRows, footer: supplierFooter,
    },
    {
      t: 'table', title: 'Margin range by supplier, brand and currency', src: 'ledger',
      facets: false, cols: GROUP_COLS, rows: margin.groupRows, footer: groupFooter,
    },
    {
      t: 'table', title: 'Supply offers', src: 'ledger',
      facets: false, cols: OFFER_COLS, rows: margin.offerRows, footer: offerFooter,
    },
  ]

  if (margin.lossMaking > 0) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Margin',
      title: `${String(margin.lossMaking)} offer${margin.lossMaking === 1 ? '' : 's'} priced above the shelf`,
      body: 'The supplier’s list price is higher than the face value we sell at, so each unit sold loses money. Re-price the shelf item or re-source it.',
      ref: 'margin < 0',
    })
  }

  const missing = [...MARGIN_MISSING, ...summarise(gaps)]
  if (unreadableSuppliers > 0) missing.push(`${String(unreadableSuppliers)} supplier(s) this console could not read`)
  if (margin.unreadableGroups > 0) missing.push(`${String(margin.unreadableGroups)} margin group(s) this console could not read`)
  if (margin.unreadableOffers > 0) missing.push(`${String(margin.unreadableOffers)} supply offer(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: MARGIN_SOURCE, authority: SUPPLY_AUTHORITY,
      readAt: asOf.kind === 'at'
        ? `${asOf.label} (the backend’s own as_of)`
        : `${readAtStamp(at)} (console receipt — the response did not state its as_of)`,
      missing,
    },
    title: 'Supplier costs & margin',
    subject: `giftcard_supply_offers · ${String(margin.offerRows.length)} ${margin.offerRows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ===========================================================================
// sf-failed — money taken, no voucher, no refund
// ===========================================================================

/**
 * The read sends NO `state` parameter, and this constant is what a test asserts
 * against.
 *
 * `state=failed` is a deterministic 400 on every page load — the endpoint
 * refuses that value by name because `transitions[paid]` is
 * `{delivered, refunded}` and an order that has been charged cannot be
 * abandoned. Every other non-owed state is refused by the same error. The queue
 * this screen wants is the endpoint's default and only answer, so the honest
 * request is the bare one.
 */
export const QUEUE_QUERY: Readonly<Record<string, string>> = {}

/**
 * The state vocabulary, for the chip tone only.
 *
 * Every row in this queue is `paid` by construction. The column stays anyway:
 * a state that is constant today is the first thing to change if the supply
 * seam lands, and a queue silently rendering a `delivered` row as though it
 * were owed is the failure this screen exists to catch. A value outside the set
 * draws with an attention tone rather than a neutral one.
 */
const ORDER_STATES = new Set(['created', 'paid', 'delivered', 'failed', 'refunded'])

/**
 * `payment_path` is `card` or `balance`, and it changes what a resolution would
 * mean: a card order is refunded at the PSP, a balance order is not a
 * standalone purchase at all.
 *
 * The corpus's `wallet_balance` and `card_standalone` are not values this field
 * can hold. They are not translated to — a console that renamed the backend's
 * vocabulary to the design's would leave an operator quoting a word that
 * appears in no log and no query.
 */
const PATH_TONE: Readonly<Record<string, Tone>> = { card: 'a', balance: 'n' }

const QUEUE_COLS: readonly Column[] = [
  ['Order', 'm', '110px'],
  ['Buyer', 's', 'minmax(180px,1.3fr)'],
  ['Brand', 'x', '140px'],
  ['Item', 'm', 'minmax(150px,1.1fr)'],
  ['Qty', 'n', '70px'],
  ['Charged', 'a', '146px'],
  ['of which VAT', 'a', '140px'],
  ['Paid by', 'c', '116px'],
  ['State', 'c', '104px'],
  ['Payment ref', 'm', 'minmax(170px,1.2fr)'],
  ['Waiting', 'x2', '108px'],
]

interface OrderRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  readonly orderRef: string | null
}

/**
 * Who bought it, from what the row actually carries.
 *
 * There is no customer NAME on this wire and the corpus draws one. What exists
 * is a `customer_id` — ABSENT for a standalone buyer, and that absence IS the
 * standalone mode rather than a missing field — and the buyer's own email,
 * which for a standalone purchase is the only identifier there is. Dropping it
 * would leave exactly the rows nobody can chase unidentifiable, and the
 * endpoint carries its own permission grant because of it.
 */
function buyerCell(o: Record<string, unknown>, gaps: string[]): Cell {
  const id = readInt(o, 'customer_id')
  const email = readText(o, 'buyer_email')
  if (id.kind === 'unreadable') gaps.push('a customer id this console could not state')
  if (email === null) gaps.push('a buyer email')

  if (id.kind === 'exact') return [`customer ${String(id.value)}`, email ?? '— no email on the row']
  // `absent` is the standalone buyer and says so in words; `unreadable` is a
  // failure and must not read the same.
  const lead = id.kind === 'absent' ? 'standalone buyer · no wallet' : '— customer id unreadable'
  return [lead, email ?? '— no email on the row']
}

function readOrder(raw: unknown): OrderRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const ref = readText(o, 'order_ref')
  if (ref === null) gaps.push('an order reference')

  const brand = readText(o, 'brand')
  if (brand === null) gaps.push('a brand')
  const item = readText(o, 'item_key')
  if (item === null) gaps.push('an item key')

  const qty = readInt(o, 'quantity')
  if (qty.kind !== 'exact') gaps.push('a quantity')

  const currency = readText(o, 'currency')
  if (currency === null || !isCurrencyCode(currency)) gaps.push('a currency on an order')
  if (exactMinor(o['gross_minor']) === null) gaps.push('a charged amount')
  if (exactMinor(o['vat_minor']) === null) gaps.push('a VAT amount')

  const pathRaw = readText(o, 'payment_path')
  const pathKnown = pathRaw !== null && knows(PATH_TONE, pathRaw)
  if (!pathKnown) gaps.push('a payment path this console recognises')
  const pathCell: ChipCell = pathKnown
    ? [pathRaw, PATH_TONE[pathRaw] ?? 'a']
    : [pathRaw ?? 'unstated', 'a']

  const stateRaw = readText(o, 'state')
  if (stateRaw === null) gaps.push('an order state')
  const stateCell: ChipCell = stateRaw === null
    ? ['unstated', 'a']
    : [stateRaw, ORDER_STATES.has(stateRaw) ? 'a' : 'r']

  // The PSP's own opaque reference. Evidence that money moved, never a claim
  // that we moved it — and the nearest thing this row has to law 2's provider
  // reference. A row without one has had money taken with nothing to quote.
  const payRef = readText(o, 'payment_ref')
  const paySource = readText(o, 'payment_source')
  if (payRef === null) gaps.push('a payment reference')
  const refCell = payRef === null
    ? '— none on the row'
    : paySource === null ? payRef : `${paySource} · ${payRef}`

  // Server-computed, so no ambient clock here. This is the column the queue is
  // sorted by and the reason it is a queue: how long the money has been sitting
  // in nothing.
  const age = readDuration(o, 'age_seconds')
  if (age.kind !== 'exact') gaps.push('how long an order has been owed')

  return {
    cells: [
      ref ?? '— no reference',
      buyerCell(o, gaps),
      brand ?? '—',
      item ?? '—',
      qty.kind === 'exact' ? String(qty.value) : '—',
      amountCell(o['gross_minor'], currency),
      amountCell(o['vat_minor'], currency),
      pathCell,
      stateCell,
      refCell,
      age.kind === 'exact' ? spanOf(age.value) : '—',
    ],
    gaps,
    orderRef: ref,
  }
}

/**
 * The four designed columns this read cannot answer for.
 *
 * The first is refused by the endpoint BY NAME and the refusal is the feature;
 * the middle two are constant by construction as well as absent from the wire.
 */
const QUEUE_MISSING: readonly string[] = [
  'a debit posting, account or wallet reference — a standalone card purchase touches no ledger and produces no posting, so there is none to show',
  'voucher state — no field, and every row in this queue is un-vouchered by construction',
  'refund state — no field, and no refund-instruction record exists: an order goes from paid to delivered or refunded, with nothing in between',
  'the supplier error — carried on the order’s own history, which a row opens into',
]

export function composeStorefrontQueue(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Charged and not delivered', 'The queue could not be read',
      'Nothing below is known. That is NOT the invariant holding — an unread queue and an empty one look identical and mean opposite things.',
      read, QUEUE_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const orders = o === null ? null : asArray(o['orders'])
  if (o === null || orders === null) {
    return unread(
      'Charged and not delivered', 'The queue could not be read',
      'The queue returned a shape this console does not know.',
      { ok: false, reason: 'The queue returned a shape this console does not know.', decisionRef: null, refused: false },
      QUEUE_SOURCE,
    )
  }

  const gaps: string[] = []
  let unreadable = 0
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  for (const raw of orders) {
    const row = readOrder(raw)
    if (row === null) unreadable += 1
    else {
      rows.push(row.cells)
      // The identity travels BESIDE the drawing rather than inside it: the
      // first cell also holds "— no reference" for a row whose ref did not
      // arrive, and an opener reading the ref back out of the cell would
      // request that string and report whatever answered as this operator's
      // order.
      rowKeys.push(row.orderRef)
      gaps.push(...row.gaps)
    }
  }

  const hasMore = readBool(o, 'has_more') === true

  // Three different sentences, and the first is the one this screen exists to
  // be able to say. An empty queue IS the invariant holding — every debit has a
  // voucher or a refund against it — and that is only true when the read
  // succeeded and every row on it parsed.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No order has money taken with neither a voucher nor a refund against it. The invariant is holding, and this queue was read to confirm it.'
        : `No row on this page could be read: ${String(unreadable)} came back in a shape this console does not know. This is an unread queue, not an empty one.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'order' : 'orders'} charged with nothing delivered${hasMore ? ' · the backend caps the page, so this is not the whole queue' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read at all`}`

  const blocks: Block[] = [{
    t: 'table',
    title: 'Charged, with neither a voucher nor a refund',
    // In the backend's own order (`ORDER BY o.id DESC`), and this console does
    // not re-sort. Re-ordering here would silently disagree with the ordering
    // the endpoint documents and tests, and an operator comparing two pages
    // would be comparing two different sequences.
    note: 'In the order the backend returned; this console does not re-sort.',
    src: 'ledger',
    // The corpus draws `debit_taken=true`, `voucher=none`, `refund=none` as
    // applied facets. None of the three is a filter this read takes, and all
    // three are true of every row by construction — a facet bar here would
    // claim a narrowing that neither happened nor could.
    facets: false,
    cols: QUEUE_COLS,
    rows,
    footer,
  }]

  // The response carries a REQUIRED `resolution_note` saying nothing in this
  // build can resolve one of these rows. It is reflected rather than quoted —
  // the note is far longer than a screen note may be, and `withoutRoadmapNotes`
  // would drop it whole — but it is only asserted when the backend actually
  // sent it, so this console never states a fact of its own as the backend's.
  //
  // It changes what an operator does next: a queue nobody can act on reads as a
  // backlog somebody is ignoring, and "nobody has got to these" and "nothing
  // can get to these" call for opposite responses. That is also why there is no
  // resolve, retry or refund control anywhere on this screen — a button that
  // cannot work is a fabricated capability, which is the control-shaped version
  // of a fabricated figure.
  if (rows.length > 0 && readText(o, 'resolution_note') !== null) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Resolution',
      title: 'This queue has no exit in this build',
      body: 'The backend states that nothing here can deliver, fail or refund one of these orders: all three wait on the gift-card supply integration. These rows are not a backlog anybody is behind on.',
      ref: 'resolution_note · go-wallet#78',
    })
  }

  const missing = [...QUEUE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} order row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: QUEUE_SOURCE, authority: QUEUE_AUTHORITY,
      // Neither this route nor the point lookup states when it read its rows,
      // so the stamp is the instant the console received the answer and is
      // labelled as that rather than passed off as a read time.
      readAt: `${readAtStamp(at)} (console receipt — the endpoint does not state when it read)`,
      missing,
    },
    // NOT the corpus's "Failed & refund queue", and the difference matters more
    // than a heading usually does. These orders are `paid`: charged, and not
    // yet delivered. The backend REFUSES the word `failed` for this population
    // by name — `transitions[paid]` is {delivered, refunded}, so a charged
    // order cannot be abandoned — and an operator who reads "failed" reasonably
    // goes looking for a retry, which does not exist on this screen or anywhere
    // else in this build. The rail still says "Failed & refund queue" because
    // the IA is the design contract; the screen says what it is actually
    // showing, which is what `ScreenData.title` is for.
    title: 'Charged and not delivered',
    purpose:
      'Orders where money was taken and there is neither a voucher nor a refund. '
      + 'These are not “failed” orders: a charged order goes to delivered or refunded and '
      + 'cannot be abandoned, so this state is the alarm rather than an outcome.',
    subject: `storefront_orders · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

// ---------------------------------------------------------------------------
// One order, and its transition history — the row opener
// ---------------------------------------------------------------------------

const HISTORY_COLS: readonly Column[] = [
  ['From', 'c', '116px'],
  ['To', 'c', '116px'],
  ['At', 'm', '150px'],
  ['Actor', 'x', '140px'],
  ['Reason', 'm', '150px'],
  ['Detail', 'x', 'minmax(200px,1.5fr)'],
  ['External ref', 'm', 'minmax(160px,1.1fr)'],
]

function orderSummary(o: Record<string, unknown>, gaps: string[]): KvBlock {
  const currency = readText(o, 'currency')
  const gross = amountCell(o['gross_minor'], currency)
  const net = amountCell(o['net_minor'], currency)
  const vat = amountCell(o['vat_minor'], currency)
  if (!isCurrencyCode(currency)) gaps.push('a currency on the order')

  // A `kv` item is `[key, value, chip, tone, note, font]` and its value is a
  // string, so the amounts are rendered through one helper rather than as bare
  // numbers — law 8 holds outside table cells too.
  const asText = (cell: Cell): string =>
    Array.isArray(cell) && typeof cell[0] === 'number' && typeof cell[1] === 'string'
      ? `${String(cell[0])} minor · ${cell[1]}`
      : typeof cell === 'string' ? cell : '—'

  const paid = readInstant(o, 'paid_at')
  const age = readDuration(o, 'age_seconds')
  const path = readText(o, 'payment_path')
  const state = readText(o, 'state')
  const payRef = readText(o, 'payment_ref')
  const paySource = readText(o, 'payment_source')

  const items: KvItem[] = [
    ['Order', readText(o, 'order_ref') ?? '— no reference', '', 'n', '', 'm'],
    ['State', state ?? '— unstated', '', state === null ? 'a' : 'n', '', ''],
    ['Brand · item', `${readText(o, 'brand') ?? '—'} · ${readText(o, 'item_key') ?? '—'}`, '', 'n', '', ''],
    ['Charged', asText(gross), '', 'n', 'VAT inclusive, as the buyer was charged', 'b'],
    ['of which net', asText(net), '', 'n', '', ''],
    ['of which VAT', asText(vat), '', 'n', '', ''],
    ['Paid by', path ?? '— unstated', '', path === null ? 'a' : 'n', 'card is refunded at the PSP; balance is not a standalone purchase', ''],
    [
      'Payment reference',
      payRef === null ? '— none on the row' : paySource === null ? payRef : `${paySource} · ${payRef}`,
      '', payRef === null ? 'a' : 'n',
      // Law 2, and the endpoint refuses a posting by name: what this row
      // carries is the payer's own opaque reference, which is evidence that
      // money moved and never a claim that we moved it.
      'the payer’s own reference — there is no posting, account or wallet on a standalone card purchase',
      'm',
    ],
    ['Paid at', paid.kind === 'at' ? paid.label : '— unstated', '', 'n', '', 'm'],
    ['Owed for', age.kind === 'exact' ? spanOf(age.value) : '—', '', age.kind === 'exact' ? 'a' : 'n', '', ''],
  ]
  return { t: 'kv', title: 'The order', src: 'ledger', items }
}

export function composeStorefrontOrder(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Order', 'This order could not be read',
      'Nothing about it is known — including whether it exists.',
      read, QUEUE_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const order = o === null ? null : asRecord(o['order'])
  const history = o === null ? null : asArray(o['history'])
  if (o === null || order === null || history === null) {
    return unread(
      'Order', 'This order could not be read',
      'The order read returned a shape this console does not know.',
      { ok: false, reason: 'The order read returned a shape this console does not know.', decisionRef: null, refused: false },
      QUEUE_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  for (const raw of history) {
    const e = asRecord(raw)
    if (e === null) { unreadable += 1; continue }

    // `from_state` is `omitempty` and is legitimately absent on the first
    // transition — an order coming into existence has no state it came from. It
    // reads as that rather than as a dash.
    const from = readText(e, 'from_state')
    const to = readText(e, 'to_state')
    if (to === null) gaps.push('a destination state on a transition')
    const at2 = readInstant(e, 'occurred_at')
    if (at2.kind !== 'at') gaps.push('when a transition occurred')
    const actor = readText(e, 'actor')
    const reason = readText(e, 'reason_code')

    const fromCell: ChipCell = from === null
      ? ['created', 'n']
      : [from, ORDER_STATES.has(from) ? 'n' : 'a']
    const toCell: ChipCell = to === null
      ? ['unstated', 'a']
      : [to, ORDER_STATES.has(to) ? 'n' : 'a']

    rows.push([
      fromCell,
      toCell,
      at2.kind === 'at' ? at2.label : '—',
      actor ?? '— unattributed',
      reason ?? '— none',
      readText(e, 'detail') ?? '',
      // The supplier's or PSP's reference. Not a posting id — see the module
      // header and the summary block above.
      readText(e, 'external_ref') ?? '',
    ])
  }

  const blocks: Block[] = [
    orderSummary(order, gaps),
    {
      t: 'table', title: 'Transitions', src: 'ledger', facets: false,
      cols: HISTORY_COLS, rows,
      footer: rows.length === 0
        ? (unreadable === 0
            ? 'No transition has been recorded against this order.'
            : `No transition could be read: ${String(unreadable)} came back in a shape this console does not know.`)
        : `${String(rows.length)} ${rows.length === 1 ? 'transition' : 'transitions'} · append-only, and there is no control here that adds one`,
    },
  ]

  const missing = [
    'a debit posting, account or wallet reference — a standalone card purchase produces none',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} transition(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: QUEUE_SOURCE, authority: QUEUE_AUTHORITY,
      readAt: `${readAtStamp(at)} (console receipt — the endpoint does not state when it read)`,
      missing,
    },
    title: `Order ${readText(order, 'order_ref') ?? '— no reference'}`,
    subject: `storefront_orders · 1 row`,
  }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export const loadStorefrontCatalog: ScreenLoader = async (api) =>
  composeStorefrontCatalog(
    await attempt(() => api.get<unknown>(STOREFRONT_ITEMS_PATH), 'The storefront catalogue'),
    new Date(),
  )

export const loadStorefrontMargin: ScreenLoader = async (api) =>
  composeStorefrontMargin(
    await attempt(() => api.get<unknown>(STOREFRONT_MARGIN_PATH), 'The supply catalogue'),
    new Date(),
  )

/**
 * The owed-order queue.
 *
 * No `state` in the query, deliberately — see `QUEUE_QUERY`. The endpoint's
 * only answer is the owed set, and asking for `failed` earns a 400 quoting the
 * transition table.
 */
export const loadStorefrontQueue: ScreenLoader = async (api) =>
  composeStorefrontQueue(
    await attempt(
      () => api.get<unknown>(STOREFRONT_ORDERS_PATH, { query: QUEUE_QUERY }),
      'The storefront order queue',
    ),
    new Date(),
  )

/** One order and its history, opened from a queue row. */
export async function readStorefrontOrder(api: ApiClient, orderRef: string): Promise<ScreenData> {
  return composeStorefrontOrder(
    await attempt(() => api.get<unknown>(storefrontOrderPath(orderRef)), 'The order'),
    new Date(),
  )
}
